// What a "Coming soon" screen (Shop, Orders, Cart) says and offers: who and which school the parent
// is shopping for, and the one thing they can do now that ordering will need (a child, then a
// delivery address). No invented dates, products or prices (PRODUCT.md, Evidence on Hand).
import { Link } from "expo-router";

import { useSelectedStudent } from "@/profile/selection";
import { useParentData } from "@/profile/useParentData";
import { standardClassName } from "@/profile/viewModels";
import { Button } from "@/ui/Button";

export type ComingSoonContext = {
  /** "Aarav at Greenfield Public School (Class 1)" or null before a child is linked. */
  who: string | null;
  action: {
    label: string;
    href: "/addresses/new" | "/enter-school-code?mode=add";
  } | null;
};

export function useComingSoonContext(): ComingSoonContext {
  const { students, addresses } = useParentData();
  const selectedId = useSelectedStudent();
  const linked = (students.data ?? []).filter(
    (s) => s.linked && s.school !== null,
  );
  const selected = linked.find((s) => s.id === selectedId) ?? linked[0] ?? null;
  if (!selected?.school)
    return {
      who: null,
      action: students.data
        ? { label: "Add a child", href: "/enter-school-code?mode=add" }
        : null,
    };
  const first = selected.name.split(" ")[0];
  const who = `${first} at ${selected.school.name} (${standardClassName(selected.classLevel ?? "")})`;
  const hasAddress = (addresses.data ?? []).length > 0;
  return {
    who,
    action:
      addresses.data && !hasAddress
        ? { label: "Add delivery address", href: "/addresses/new" }
        : null,
  };
}

/** The single useful action, when there is one. */
export function ComingSoonAction({ context }: { context: ComingSoonContext }) {
  if (!context.action) return null;
  return (
    <Link href={context.action.href} asChild>
      <Button accessibilityRole="link">{context.action.label}</Button>
    </Link>
  );
}

/** "Coming soon. <what>" naming the child and school when known. */
export function comingSoonLead(what: string, who: string | null): string {
  return who ? `Coming soon for ${who}. ${what}` : `Coming soon. ${what}`;
}
